import { creatorbase } from '@round/api';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { asMoneyWithoutZeros } from 'helpers';
import styles from './PurchaseOrderInfo.module.css';
import { SkeletonizedValue } from '@round/ui-kit';
import cn from 'classnames';
import { useTeam } from '@round/creatorbase-helpers/api-hooks';

type Props = {
    purchaseOrder: creatorbase.PurchaseOrder | null | undefined;
    isLoading: boolean;
    error?: string | null;
};

const PurchaseOrderInfo = ({ purchaseOrder, isLoading, error }: Props) => {
    const { getCurrencyById } = useCurrencies();
    const currency = getCurrencyById(purchaseOrder?.currency_id);

    const poNumber = purchaseOrder?.number || purchaseOrder?.request_reference || '-';
    const totalAmount = asMoneyWithoutZeros(purchaseOrder?.amount, currency);
    const allocatedAmount = asMoneyWithoutZeros(
        (purchaseOrder?.amount ?? 0) - (purchaseOrder?.remaining_amount ?? 0),
        currency
    );
    const availableAmount = asMoneyWithoutZeros(purchaseOrder?.remaining_amount, currency);

    const { data: team, isLoading: isTeamLoading } = useTeam(purchaseOrder?.team_id);

    if (error) {
        return (
            <div className={styles.container}>
                <p className={styles.error}>{error}</p>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.purchaseOrder}>
                <div className={styles.poHeader}>
                    <span className={styles.poNumber}>
                        <SkeletonizedValue isInitialized={!isLoading}>{poNumber}</SkeletonizedValue>
                    </span>

                    {purchaseOrder?.invoice_status && (
                        <div className={cn(styles.badge, styles[purchaseOrder.invoice_status.toLowerCase()])}>
                            <b>{purchaseOrder.invoice_number ? purchaseOrder.invoice_number : null}</b>
                        </div>
                    )}
                </div>
                <p className={styles.poDescription}>
                    <SkeletonizedValue isInitialized={!isLoading}>
                        {purchaseOrder?.description || '-'}
                    </SkeletonizedValue>
                </p>

                <p className={styles.poTeam}>
                    <SkeletonizedValue isInitialized={!isLoading && !isTeamLoading}>
                        {team?.name || '-'}
                    </SkeletonizedValue>
                </p>
            </div>

            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Total Amount:</span>
                <span className={styles.amountRowValue}>
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {totalAmount}
                    </SkeletonizedValue>
                </span>
            </p>
            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Allocated:</span>
                <span className={styles.amountRowValue}>
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {allocatedAmount}
                    </SkeletonizedValue>
                </span>
            </p>
            <p className={styles.amountRow}>
                <span className={styles.amountRowLabel}>Available:</span>
                <span
                    className={cn(styles.amountRowValue, {
                        [styles.statusClear]: purchaseOrder?.remaining_amount === 0,
                        [styles.statusGood]:
                            typeof purchaseOrder?.remaining_amount === 'number' && purchaseOrder?.remaining_amount > 0,
                    })}
                >
                    <SkeletonizedValue isInitialized={!isLoading} width="4rem">
                        {availableAmount}
                    </SkeletonizedValue>
                </span>
            </p>
        </div>
    );
};

export default PurchaseOrderInfo;
